import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  AlertTriangle,
  CalendarClock,
  Check,
  CreditCard,
  MapPin,
  MessageSquareWarning,
  Package,
  StickyNote,
} from "lucide-react";

import { CustomButton } from "../components/CustomButton";
import { TimeSlotPicker, SlotSelection } from "../components/TimeSlotPicker";
import { useToast } from "../components/ToastProvider";
import { PENDING_PAYMENT_ORDER_KEY } from "../components/PaymentReturnHandler";
import { useOrderStore } from "../features/orders/orderStore";
import { Order } from "../features/orders/types";
import {
  STATUS_LABELS,
  STATUS_STEPS,
  formatDateTime,
  formatMoney,
  formatSlot,
} from "../utils/orderFormat";
import rushLogo from "../assets/png/rush-logo.png";

const IMG_URL = process.env.REACT_APP_API_IMG_URL || "";

const PAYMENT_BADGE: Record<Order["payment_status"], string> = {
  paid: "text-green-700 bg-green-50",
  partial: "text-orange-700 bg-orange-50",
  unpaid: "text-gray-600 bg-gray-100",
};

// Bottom sheet used for "Report an issue" and "Choose delivery time"
const Sheet: React.FC<{
  open: boolean;
  title: string;
  onClose: () => void;
  children: React.ReactNode;
}> = ({ open, title, onClose, children }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-end md:items-center justify-center bg-black/40 md:p-6" onClick={onClose}>
      <div
        className="bg-white rounded-t-3xl md:rounded-3xl w-full max-w-md shadow-xl p-6 space-y-4 max-h-[85vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex justify-center -mt-2 md:hidden">
          <div className="w-10 h-1 rounded-full bg-gray-200" />
        </div>
        <h3 className="text-lg font-bold text-gray-900">{title}</h3>
        {children}
      </div>
    </div>
  );
};

const Section: React.FC<{ title?: string; className?: string; children: React.ReactNode }> = ({
  title,
  className = "",
  children,
}) => (
  <div className={`bg-white rounded-2xl border border-gray-100 p-4 shadow-sm ${className}`}>
    {title && (
      <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider mb-3">{title}</p>
    )}
    {children}
  </div>
);

export default function OrderDetailsScreen() {
  const navigate = useNavigate();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const orderId = searchParams.get("order");

  const {
    currentOrder: order,
    isLoading,
    error,
    fetchOrder,
    confirmPayment,
    approveOrder,
    reportOrderIssue,
    setDeliverySlot,
  } = useOrderStore();

  const [busy, setBusy] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [reportText, setReportText] = useState("");
  const [slotOpen, setSlotOpen] = useState(false);
  const [deliverySlot, setDeliverySlotValue] = useState<SlotSelection>(null);
  const [photo, setPhoto] = useState<string | null>(null);
  const handledReturn = useRef(false);

  const load = useCallback(() => {
    if (orderId) fetchOrder(orderId).catch(() => {});
  }, [orderId, fetchOrder]);

  useEffect(() => {
    load();
  }, [load]);

  // Back from the SkipCash payment page: ?order=..&id=<paymentId>&statusId=..
  useEffect(() => {
    const statusId = searchParams.get("statusId");
    if (!statusId || handledReturn.current || !orderId) return;
    handledReturn.current = true;
    try {
      localStorage.removeItem(PENDING_PAYMENT_ORDER_KEY);
    } catch {
      // nothing to clear
    }

    if (statusId === "2") {
      showToast("Payment successful. Thank you!", "success");
      // Ask the server to check the payment with SkipCash now (the webhook can
      // be slow), then keep refreshing until it shows as paid
      const paymentId = searchParams.get("id");
      void confirmPayment(orderId, paymentId).catch(() => {});
      let attempts = 0;
      const timer = setInterval(async () => {
        attempts += 1;
        await fetchOrder(orderId).catch(() => {});
        const latest = useOrderStore.getState().currentOrder;
        if (latest?.payment_status === "paid" || attempts >= 5) clearInterval(timer);
      }, 2000);
    } else if (statusId === "3") {
      showToast("Payment failed. Please try again.", "error");
    } else if (statusId === "4") {
      showToast("Payment cancelled.", "info");
    }
    setSearchParams({ order: orderId }, { replace: true });
  }, [searchParams, setSearchParams, orderId, fetchOrder, confirmPayment, showToast]);

  const run = async (action: () => Promise<unknown>, success: string) => {
    setBusy(true);
    try {
      await action();
      showToast(success, "success");
      return true;
    } catch (e) {
      showToast(e instanceof Error ? e.message : "Something went wrong", "error");
      return false;
    } finally {
      setBusy(false);
    }
  };

  const handleApprove = () =>
    order && run(() => approveOrder(order.id), "Thanks! We've started cleaning your items.");

  const handleReport = async () => {
    if (!order || !reportText.trim()) return;
    const ok = await run(
      () => reportOrderIssue(order.id, reportText.trim()),
      "Thanks for letting us know. We'll review and get back to you.",
    );
    if (ok) {
      setReportOpen(false);
      setReportText("");
    }
  };

  const handleBookDelivery = async () => {
    if (!order || !deliverySlot) return;
    const ok = await run(
      () => setDeliverySlot(order.id, deliverySlot.slotId, deliverySlot.date),
      "Delivery time booked",
    );
    if (ok) setSlotOpen(false);
  };

  const handlePay = () => {
    if (!order?.payment_link) return;
    try {
      localStorage.setItem(PENDING_PAYMENT_ORDER_KEY, order.id);
    } catch {
      // Without it the return still works, just lands on the orders list
    }
    window.location.href = order.payment_link;
  };

  const Header = (
    <div className="md:hidden sticky top-0 z-40 h-[76px] bg-white/90 backdrop-blur-md px-6 pt-4 pb-4 flex items-center justify-between">
      <button
        onClick={() => navigate(-1)}
        className="w-11 h-11 border border-gray-100 rounded-2xl flex items-center justify-center bg-white shadow-sm"
        aria-label="Back"
      >
        <ArrowLeft size={20} className="text-gray-700" />
      </button>
      <img src={rushLogo} alt="Rush" className="h-7 w-auto object-contain" />
    </div>
  );

  if (!orderId) {
    return (
      <div className="min-h-screen bg-gray-50">
        {Header}
        <p className="text-center text-sm text-gray-500 mt-20">Order not found.</p>
      </div>
    );
  }

  if (!order || String(order.id) !== String(orderId)) {
    return (
      <div className="min-h-screen bg-gray-50">
        {Header}
        {isLoading ? (
          <div className="flex justify-center mt-20">
            <div className="w-8 h-8 border-4 border-[#44B6CE] border-t-transparent animate-spin rounded-full" />
          </div>
        ) : (
          <div className="text-center mt-20 px-8">
            <p className="text-sm text-gray-500">{error || "Order not found."}</p>
            <button onClick={load} className="text-sm font-semibold text-[#44B6CE] mt-3">
              Try again
            </button>
          </div>
        )}
      </div>
    );
  }

  const items = order.items ?? [];
  const isLocker = order.pickup_type === "locker";
  const currentStep = STATUS_STEPS.indexOf(order.status);
  const historyTime = (status: string) => {
    const entries = (order.status_history ?? []).filter((h) => h.to_status === status);
    return entries.length ? formatDateTime(entries[entries.length - 1].createdAt) : "";
  };
  // A locker bag is never "collected from home", so skip that wording
  const stepLabel = (status: (typeof STATUS_STEPS)[number]) =>
    isLocker && status === "ready_to_pickup" ? "Courier assigned" : STATUS_LABELS[status];

  const canBookDelivery =
    order.pickup_type === "home" &&
    order.status !== "delivered" &&
    currentStep >= STATUS_STEPS.indexOf("received");
  const awaitingApproval = order.approval_status === "pending";
  const canPay = order.amount_due > 0 && !!order.payment_link && order.approval_status !== "disputed";

  return (
    <div className="min-h-screen md:min-h-0 bg-gray-50 md:bg-transparent pb-10 md:pb-0">
      {Header}

      <div className="bg-white px-6 pb-6 md:pt-6 shadow-sm rounded-b-[32px] md:rounded-3xl mb-5 md:mb-6">
        <p className="text-xs font-semibold text-gray-400 uppercase tracking-wider">Order</p>
        <h1 className="text-2xl font-black text-gray-900">#{order.order_no}</h1>
        <p className="text-sm font-semibold text-[#44B6CE] mt-1">{stepLabel(order.status)}</p>
      </div>

      {/* Phones: one column (order-* keeps the reading order). Desktop: two columns. */}
      <div className="px-5 flex flex-col gap-4 lg:px-0 lg:grid lg:grid-cols-5 lg:gap-6 lg:items-start">
        <div className="contents lg:flex lg:flex-col lg:gap-4 lg:col-span-3">
        {/* ── What the customer needs to do ── */}
        {awaitingApproval && (
          <div className="order-1 bg-[#44B6CE]/10 border border-[#44B6CE]/30 rounded-2xl p-4 space-y-3">
            <div>
              <p className="font-bold text-gray-900">Please review your items</p>
              <p className="text-sm text-gray-600 mt-1">
                We've listed what we found in your bag. Approve to start cleaning, or let us know if
                something isn't right.
              </p>
              {order.has_condition_notes && (
                <p className="text-sm text-amber-700 mt-2 flex gap-2">
                  <AlertTriangle size={16} className="flex-shrink-0 mt-0.5" />
                  Our staff noted the condition of some items. Please check them below.
                </p>
              )}
            </div>
            {canPay ? (
              <>
                <CustomButton variant="primary" className="py-3 font-bold" onClick={handlePay} disabled={busy}>
                  Approve & Pay {formatMoney(order.amount_due)}
                </CustomButton>
                <CustomButton variant="outlinewithcolor" className="py-3 text-base" onClick={handleApprove} disabled={busy}>
                  Approve, pay on delivery
                </CustomButton>
              </>
            ) : (
              <CustomButton variant="primary" className="py-3 font-bold" onClick={handleApprove} disabled={busy}>
                Approve
              </CustomButton>
            )}
            <button
              onClick={() => setReportOpen(true)}
              className="w-full text-sm font-semibold text-gray-600 py-1"
              disabled={busy}
            >
              Something's not right? Report an issue
            </button>
          </div>
        )}

        {order.approval_status === "disputed" && (
          <div className="order-1 bg-amber-50 border border-amber-200 rounded-2xl p-4">
            <p className="font-bold text-amber-800 flex items-center gap-2">
              <MessageSquareWarning size={18} /> We're looking into it
            </p>
            {order.dispute_note && (
              <p className="text-sm text-amber-800 mt-2 italic">"{order.dispute_note}"</p>
            )}
            <p className="text-sm text-amber-700 mt-2">
              We'll send you an updated list shortly. Nothing will be cleaned until you approve it.
            </p>
          </div>
        )}

        {!awaitingApproval && canPay && (
          <div className="order-1 bg-white rounded-2xl border border-gray-100 p-4 shadow-sm space-y-3">
            <div className="flex items-center justify-between">
              <p className="font-bold text-gray-900">Amount due</p>
              <p className="text-lg font-black text-[#44B6CE]">{formatMoney(order.amount_due)}</p>
            </div>
            <CustomButton variant="primary" className="py-3 font-bold" onClick={handlePay}>
              <span className="flex items-center justify-center gap-2">
                <CreditCard size={18} /> Pay now
              </span>
            </CustomButton>
            <p className="text-xs text-gray-400 text-center">Or pay the courier on delivery.</p>
          </div>
        )}

        {order.approval_status === "not_sent" && items.length === 0 && order.status !== "delivered" && (
          <div className="order-1 bg-white rounded-2xl border border-gray-100 p-4 shadow-sm flex gap-3">
            <Package className="text-[#44B6CE] flex-shrink-0" size={20} />
            <p className="text-sm text-gray-600">
              {isLocker && order.status === "open"
                ? "Drop your bag in the locker. Once it reaches our shop we'll list the items and send you the total."
                : "Once your bag reaches our shop we'll list the items and send you the total."}
            </p>
          </div>
        )}

        {/* ── Items ── */}
        {items.length > 0 && (
          <Section title="Items" className="order-3">
            <div className="divide-y divide-gray-50">
              {items.map((item) => (
                <div key={item.id} className="py-2.5 first:pt-0">
                  <div className="flex justify-between gap-3">
                    <div className="min-w-0">
                      <p className="text-sm font-semibold text-gray-900">
                        {item.name} <span className="text-gray-400 font-normal">x{item.quantity}</span>
                      </p>
                      <p className="text-xs text-gray-500">{item.service}</p>
                    </div>
                    <p className="text-sm font-semibold text-gray-900 whitespace-nowrap">
                      {formatMoney(item.line_total)}
                    </p>
                  </div>
                  {item.condition_note && (
                    <div className="mt-2 bg-amber-50 rounded-xl px-3 py-2 flex gap-3 items-start">
                      <AlertTriangle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
                      <p className="text-xs text-amber-800 flex-1">{item.condition_note}</p>
                      {item.condition_photo && (
                        <button
                          onClick={() => setPhoto(`${IMG_URL}/order-items/${item.condition_photo}`)}
                          className="flex-shrink-0"
                          aria-label="View photo"
                        >
                          <img
                            src={`${IMG_URL}/order-items/${item.condition_photo}`}
                            alt="Item condition"
                            className="w-10 h-10 rounded-lg object-cover"
                          />
                        </button>
                      )}
                    </div>
                  )}
                </div>
              ))}
            </div>

            <div className="border-t border-gray-100 mt-2 pt-3 space-y-1.5 text-sm">
              <div className="flex justify-between text-gray-500">
                <span>Subtotal</span>
                <span>{formatMoney(order.subtotal)}</span>
              </div>
              {Number(order.discount_amount) > 0 && (
                <div className="flex justify-between text-green-600">
                  <span>Discount{order.promo_code ? ` (${order.promo_code})` : ""}</span>
                  <span>-{formatMoney(order.discount_amount)}</span>
                </div>
              )}
              <div className="flex justify-between font-bold text-gray-900">
                <span>Total</span>
                <span>{formatMoney(order.amount)}</span>
              </div>
              {order.amount_paid > 0 && (
                <div className="flex justify-between text-gray-500">
                  <span>Paid</span>
                  <span>-{formatMoney(order.amount_paid)}</span>
                </div>
              )}
              <div className="flex justify-between items-center pt-1">
                <span
                  className={`text-xs font-bold px-2 py-0.5 rounded-lg capitalize ${PAYMENT_BADGE[order.payment_status]}`}
                >
                  {order.payment_status}
                </span>
                {order.amount_due > 0 && (
                  <span className="font-bold text-[#44B6CE]">Due {formatMoney(order.amount_due)}</span>
                )}
              </div>
            </div>
          </Section>
        )}

        {/* ── Delivery time (home orders) ── */}
        {canBookDelivery && (
          <Section title="Delivery time" className="order-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-3 min-w-0">
                <CalendarClock size={18} className="text-[#44B6CE] flex-shrink-0" />
                <p className="text-sm text-gray-800">
                  {order.delivery_slot
                    ? formatSlot(order.delivery_slot, order.delivery_slot_date)
                    : "Choose when you'd like your clothes back"}
                </p>
              </div>
              <button
                onClick={() => setSlotOpen(true)}
                className="text-sm font-semibold text-[#44B6CE] flex-shrink-0"
              >
                {order.delivery_slot ? "Change" : "Choose"}
              </button>
            </div>
          </Section>
        )}

        </div>
        <div className="contents lg:flex lg:flex-col lg:gap-4 lg:col-span-2 lg:sticky lg:top-24">
        {/* ── Progress ── */}
        <Section title="Progress" className="order-2">
          <ol className="space-y-0">
            {STATUS_STEPS.map((status, i) => {
              const done = i <= currentStep;
              const time = done ? historyTime(status) : "";
              return (
                <li key={status} className="flex gap-3">
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-6 h-6 rounded-full flex items-center justify-center ${
                        done ? "bg-[#44B6CE]" : "bg-gray-100"
                      }`}
                    >
                      {done && <Check size={14} className="text-white" strokeWidth={3} />}
                    </div>
                    {i < STATUS_STEPS.length - 1 && (
                      <div className={`w-0.5 h-6 ${i < currentStep ? "bg-[#44B6CE]" : "bg-gray-100"}`} />
                    )}
                  </div>
                  <div className="flex-1 flex justify-between pb-2">
                    <p className={`text-sm ${done ? "font-semibold text-gray-900" : "text-gray-400"}`}>
                      {stepLabel(status)}
                    </p>
                    {time && <p className="text-xs text-gray-400">{time}</p>}
                  </div>
                </li>
              );
            })}
          </ol>
        </Section>

        {/* ── Order info ── */}
        <Section title="Details" className="order-5">
          <dl className="space-y-3 text-sm">
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500">Bag number</dt>
              <dd className="font-semibold text-gray-900">{order.bag_no}</dd>
            </div>
            <div className="flex justify-between gap-4">
              <dt className="text-gray-500">{isLocker ? "Locker" : "Address"}</dt>
              <dd className="font-semibold text-gray-900 text-right flex items-start gap-1">
                <MapPin size={14} className="text-gray-400 mt-0.5 flex-shrink-0" />
                {order.location}
              </dd>
            </div>
            {order.pickup_slot && (
              <div className="flex justify-between gap-4">
                <dt className="text-gray-500">Pickup</dt>
                <dd className="font-semibold text-gray-900 text-right">
                  {formatSlot(order.pickup_slot, order.pickup_slot_date)}
                </dd>
              </div>
            )}
            {order.note && (
              <div>
                <dt className="text-gray-500 flex items-center gap-1.5 mb-1">
                  <StickyNote size={14} /> Your instructions
                </dt>
                <dd className="text-gray-800 bg-gray-50 rounded-xl px-3 py-2">{order.note}</dd>
              </div>
            )}
          </dl>
        </Section>
        </div>
      </div>

      {/* ── Sheets ── */}
      <Sheet open={reportOpen} title="Report an issue" onClose={() => !busy && setReportOpen(false)}>
        <p className="text-sm text-gray-500">
          Tell us what's wrong, for example a missing item or a wrong count. We'll hold your order and
          send you an updated list.
        </p>
        <textarea
          rows={4}
          value={reportText}
          maxLength={500}
          onChange={(e) => setReportText(e.target.value)}
          placeholder="e.g. I sent 4 shirts, not 3"
          className="w-full bg-gray-50 rounded-2xl px-4 py-3 text-sm border border-gray-100 outline-none focus:border-[#44B6CE] resize-none"
        />
        <CustomButton
          variant="primary"
          className="py-3 font-bold"
          onClick={handleReport}
          disabled={busy || !reportText.trim()}
        >
          {busy ? "Sending..." : "Send"}
        </CustomButton>
      </Sheet>

      <Sheet open={slotOpen} title="Choose delivery time" onClose={() => !busy && setSlotOpen(false)}>
        <TimeSlotPicker
          type="delivery"
          value={
            deliverySlot ??
            (order.delivery_slot && order.delivery_slot_date
              ? { slotId: order.delivery_slot.id, date: order.delivery_slot_date }
              : null)
          }
          onChange={setDeliverySlotValue}
        />
        <CustomButton
          variant="primary"
          className="py-3 font-bold"
          onClick={handleBookDelivery}
          disabled={busy || !deliverySlot}
        >
          {busy ? "Booking..." : "Confirm delivery time"}
        </CustomButton>
      </Sheet>

      {photo && (
        <div
          className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-6"
          onClick={() => setPhoto(null)}
        >
          <img src={photo} alt="Item condition" className="max-w-full max-h-full rounded-2xl" />
        </div>
      )}
    </div>
  );
}
